import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Card, Badge, Button } from 'react-bootstrap';
import { CustomerLink, TransactionLink } from './EntityLinks';
import { formatDate } from '../utils/dateFormatter';

/**
 * TransactionHistory component displays a list of transactions for a vehicle
 * Reusable across VehicleDetail and VehicleFinancialLog pages
 */
export function TransactionHistory({ transactions, customers, payments }) {
  const navigate = useNavigate();

  const getCustomerName = (transaction) => {
    // First check if transaction has first_name/last_name from API join
    if (transaction.first_name && transaction.last_name) {
      return `${transaction.first_name} ${transaction.last_name}`;
    }
    
    // Fall back to looking up in customers array
    if (customers && transaction.customer_id) {
      const customer = customers.find(c => c.id === transaction.customer_id);
      if (customer) {
        return `${customer.first_name} ${customer.last_name}`;
      }
    }
    
    // Last resort: show customer ID
    return `Customer ${transaction.customer_id}`;
  };

  const getTransactionAmount = (transaction) => {
    // Total amount = rental amount + tax
    const amount = parseFloat(transaction.total_amount || transaction.amount || 0);
    const tax = parseFloat(transaction.tax_amount || 0);
    return amount + tax;
  };

  const getPaymentStatus = (transaction) => {
    // Use total_paid from backend if available, otherwise calculate from payments prop
    const totalPaid = transaction.total_paid !== undefined
      ? parseFloat(transaction.total_paid || 0)
      : (() => {
          // Fallback: calculate from payments array (legacy support)
          if (!payments || payments.length === 0) {
            return 0;
          }
          const linkedPayments = payments.filter(p => p.transaction_id === transaction.id);
          return linkedPayments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
        })();

    const invoiceAmount = parseFloat(getTransactionAmount(transaction));

    // Check if fully paid (with small tolerance for floating point)
    if (Math.abs(totalPaid - invoiceAmount) < 0.01) {
      return { status: 'complete', label: 'Complete' };
    } else if (totalPaid > 0) {
      return { status: 'partial', label: `Partial ($${totalPaid.toFixed(2)}/${invoiceAmount.toFixed(2)})` };
    } else {
      return { status: 'unpaid', label: 'Unpaid' };
    }
  };

  return (
    <Card>
      <Card.Header>
        <h3>Transaction History</h3>
      </Card.Header>
      <Card.Body className="p-0">
        {transactions && transactions.length > 0 ? (
          <Table striped hover responsive>
            <thead>
              <tr>
                <th>ID</th>
                <th>Customer</th>
                <th>Type</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Total Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(transaction => {
                const paymentStatus = getPaymentStatus(transaction);
                
                // Determine badge color based on payment status
                let statusBadgeColor = 'danger'; // unpaid - red
                if (paymentStatus.status === 'complete') {
                  statusBadgeColor = 'success'; // complete - green
                } else if (paymentStatus.status === 'partial') {
                  statusBadgeColor = 'warning'; // partial - yellow
                }

                return (
                  <tr key={transaction.id}>
                    <td>
                      <TransactionLink transactionId={transaction.id}>
                        #{transaction.id}
                      </TransactionLink>
                    </td>
                    <td>
                      <CustomerLink
                        customerId={transaction.customer_id}
                        customerName={getCustomerName(transaction)}
                      />
                    </td>
                    <td>
                      <Badge bg={transaction.type === 'rental' ? 'primary' : 'info'}>
                        {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                      </Badge>
                    </td>
                    <td>{formatDate(transaction.start_date)}</td>
                    <td>{formatDate(transaction.end_date)}</td>
                    <td>
                      <Badge bg={statusBadgeColor}>
                        {paymentStatus.label}
                      </Badge>
                    </td>
                    <td>${parseFloat(getTransactionAmount(transaction)).toFixed(2)}</td>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => navigate(`/dashboard/transactions/${transaction.id}`)}
                      >
                        View Details
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <Card.Body>
            <p className="text-muted text-center mb-0">No transactions found for this vehicle.</p>
          </Card.Body>
        )}
      </Card.Body>
    </Card>
  );
}

export default TransactionHistory;
